import React, { useState, useEffect, useMemo, useCallback } from 'react';
import logger from '../utils/logger';
import { 
  getFirestore, 
  doc, 
  updateDoc, 
  deleteDoc
} from 'firebase/firestore';
import { 
  ArchiveRestore, 
  Trash2, 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  DollarSign,
  Clock,
  AlertCircle,
  Loader2
} from 'lucide-react';
import AdminContactModal from './AdminContactModal';
import { storage, searchArchivedBillsFunction } from '../firebase';
import { ref, deleteObject } from 'firebase/storage';
import { removeDuplicateFingerprint, updateDuplicateFingerprint } from '../utils/duplicateFingerprints';

const ArchiveViewer = ({ 
  _bills = [], 
  setBills = () => {}, 
  currentUser = null,
  darkMode = false,
  setShowToast = () => {},
  setToastMessage = () => {},
  rateLimiter = null,
  refreshToken = 0,
  sessionTracker = null,
  onSecureView = null
}) => {
  const db = getFirestore();
  const [archivedBills, setArchivedBills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const ARCHIVE_FILTERS_KEY = 'utilitrack_archive_filters';
  const persistedFilters = (() => {
    try {
      const raw = localStorage.getItem(ARCHIVE_FILTERS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const [searchTerm, setSearchTerm] = useState(persistedFilters?.searchTerm || '');
  const [selectedTypes, setSelectedTypes] = useState(persistedFilters?.selectedTypes || []);
  const [dateRange, setDateRange] = useState(persistedFilters?.dateRange || { start: '', end: '' });
  const [sortBy, setSortBy] = useState(persistedFilters?.sortBy || 'archivedAt');
  const [sortOrder, setSortOrder] = useState(persistedFilters?.sortOrder || 'desc');
  const [currentPage, setCurrentPage] = useState(persistedFilters?.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(persistedFilters?.itemsPerPage || 10);
  
  const [operationInProgress, setOperationInProgress] = useState(null);
  const [adminModalState, setAdminModalState] = useState({
    isOpen: false,
    errorDescription: '',
    billId: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedBillIds, setSelectedBillIds] = useState(new Set());

  const userEmail = currentUser?.email || '';

  const toggleBillSelection = (billId) => {
    const newSelection = new Set(selectedBillIds);
    if (newSelection.has(billId)) {
      newSelection.delete(billId);
    } else {
      newSelection.add(billId);
    }
    setSelectedBillIds(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedBillIds.size === paginatedBills.length && paginatedBills.length > 0) {
      setSelectedBillIds(new Set());
    } else {
      setSelectedBillIds(new Set(paginatedBills.map(b => b.id)));
    }
  };

  const clearSelection = () => {
    setSelectedBillIds(new Set());
  };

  // Fetch archived bills via Cloud Function (server-side filtering)
  const fetchArchivedBills = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await searchArchivedBillsFunction({
        searchTerm: searchTerm || '',
        types: selectedTypes.length > 0 ? selectedTypes : [],
        dateRange: {
          start: dateRange.start || null,
          end: dateRange.end || null
        },
        sortBy: sortBy,
        sortOrder: sortOrder,
        limitCount: 500
      });
      const bills = result.data.bills || [];
      setArchivedBills(bills);
      sessionTracker?.trackRead('ArchiveViewer.fetchArchivedBills', 'bills', bills.length, { via: 'CloudFunction:searchArchivedBills', filters: { searchTerm, selectedTypes, dateRange } });
      logger.log(`[ArchiveViewer] Loaded ${bills.length} archived bills via Cloud Function`);
    } catch (error) {
      logger.error('Error fetching archived bills:', error);
      setToastMessage('Failed to load archived bills');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, selectedTypes, dateRange, sortBy, sortOrder, setToastMessage, setShowToast, sessionTracker]);

  // Fetch when filters change (debounced via useEffect)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchArchivedBills();
    }, 300); // Debounce 300ms to avoid rapid re-fetches on filter changes
    return () => clearTimeout(timer);
  }, [fetchArchivedBills, refreshToken]);

  // Bills are already filtered server-side, just apply local sort for column headers
  const filteredBills = useMemo(() => {
    let filtered = [...archivedBills];

    // Sort (server handles initial sort, but user may change sort column)
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'amount' || sortBy === 'usage') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortBy === 'date' || sortBy === 'archivedAt') {
        const aTs = aVal?.toDate ? aVal.toDate().getTime() : new Date(aVal).getTime();
        const bTs = bVal?.toDate ? bVal.toDate().getTime() : new Date(bVal).getTime();
        aVal = isNaN(aTs) ? 0 : aTs;
        bVal = isNaN(bTs) ? 0 : bTs;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [archivedBills, sortBy, sortOrder]);

  // Pagination
  const paginatedBills = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredBills.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredBills, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredBills.length / itemsPerPage);
  // Persist filters when they change
  useEffect(() => {
    try {
      localStorage.setItem(ARCHIVE_FILTERS_KEY, JSON.stringify({
        searchTerm,
        selectedTypes,
        dateRange,
        sortBy,
        sortOrder,
        currentPage,
        itemsPerPage
      }));
    } catch { void 0; }
  }, [searchTerm, selectedTypes, dateRange, sortBy, sortOrder, currentPage, itemsPerPage]);

  // Calculate stats
  const stats = useMemo(() => {
    const count = filteredBills.length;
    const totalValue = filteredBills.reduce((sum, bill) => sum + (parseFloat(bill.amount) || 0), 0);
    const timestamps = filteredBills
      .map(b => (b.archivedAt?.toDate ? b.archivedAt.toDate().getTime() : new Date(b.archivedAt).getTime()))
      .filter(ts => !isNaN(ts));
    const oldestDate = timestamps.length > 0 
      ? new Date(Math.min(...timestamps)).toLocaleDateString()
      : 'N/A';

    return { count, totalValue: totalValue.toFixed(2), oldestDate };
  }, [filteredBills]);

  // Handle restore
  const handleRestoreSingle = useCallback(async (bill) => {
    if (!rateLimiter) {
      logger.error('Rate limiter not available');
      return;
    }

    const timestamp = Date.now();
    setOperationInProgress({ type: 'restore', billId: bill.id, status: 'checking' });

    try {
      // Layer 1-4 safety check
      const canProceed = await rateLimiter.canPerformOperation(
        'RESTORE',
        bill.id,
        userEmail,
        timestamp,
        bill, // before state
        { ...bill, archived: false, archivedAt: null } // expected after state
      );

      if (!canProceed.allowed) {
        if (canProceed.skip) {
          setOperationInProgress(null);
          return;
        }

        if (canProceed.showAdminModal) {
          setAdminModalState({
            isOpen: true,
            errorDescription: canProceed.errorDescription,
            billId: bill.id
          });
        } else {
          setToastMessage(canProceed.reason);
          setShowToast(true);
        }
        setOperationInProgress(null);
        return;
      }

      setOperationInProgress({ type: 'restore', billId: bill.id, status: 'restoring' });

      // Update Firestore
      const billRef = doc(db, 'bills', bill.id);
      const now = new Date().toISOString();
      await updateDoc(billRef, {
        archived: false,
        archivedAt: null,
        restoredAt: now,
        updatedAt: now
      });

      // Re-add duplicate fingerprint for this bill
      try {
        await updateDuplicateFingerprint(db, { ...bill, archived: false });
      } catch (e) {
        logger.warn('Fingerprint re-add failed:', e);
      }

      // Record operation
      await rateLimiter.recordOperation('RESTORE', bill.id, 'success', userEmail);

      // Wait for Cloud Function to process
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update local state
      setArchivedBills(prev => prev.filter(b => b.id !== bill.id));
      setBills(prev => [...prev, { ...bill, archived: false, archivedAt: null, restoredAt: now }]);

      setToastMessage(`✓ Bill ${bill.id} restored successfully`);
      setShowToast(true);
    } catch (error) {
      logger.error('Error restoring bill:', error);
      await rateLimiter.recordOperation('RESTORE', bill.id, 'failed', userEmail);
      setToastMessage('Failed to restore bill. Please try again.');
      setShowToast(true);
    } finally {
      setOperationInProgress(null);
    }
  }, [rateLimiter, userEmail, db, setBills, setToastMessage, setShowToast]);

  const handleBulkRestore = useCallback(async () => {
    const billsToRestore = paginatedBills.filter(b => selectedBillIds.has(b.id));
    if (billsToRestore.length === 0) return;

    setOperationInProgress({ type: 'bulk-restore', status: 'processing' });
    let successCount = 0;
    let failCount = 0;

    try {
      for (const bill of billsToRestore) {
        try {
          const canProceed = await rateLimiter.canPerformOperation(
            'RESTORE',
            bill.id,
            userEmail,
            Date.now(),
            bill,
            { ...bill, archived: false, archivedAt: null }
          );

          if (!canProceed.allowed) {
            failCount++;
            continue;
          }

          const billRef = doc(db, 'bills', bill.id);
          const now = new Date().toISOString();
          await updateDoc(billRef, {
            archived: false,
            archivedAt: null,
            restoredAt: now,
            updatedAt: now
          });

          try {
            await updateDuplicateFingerprint(db, { ...bill, archived: false });
          } catch (e) {
            logger.warn('Fingerprint re-add failed:', e);
          }

          await rateLimiter.recordOperation('RESTORE', bill.id, 'success', userEmail);
          successCount++;
        } catch (error) {
          logger.error('Error restoring bill:', bill.id, error);
          failCount++;
        }
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      setArchivedBills(prev => prev.filter(b => !selectedBillIds.has(b.id)));
      setBills(prev => [...prev, ...billsToRestore.map(b => ({ ...b, archived: false, archivedAt: null, restoredAt: new Date().toISOString() }))]);

      setToastMessage(`✓ Restored ${successCount} bill(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
      setShowToast(true);
      clearSelection();
    } finally {
      setOperationInProgress(null);
    }
  }, [selectedBillIds, paginatedBills, rateLimiter, userEmail, db, setBills, setToastMessage, setShowToast]);

  const handleBulkDelete = useCallback(async () => {
    const billsToDelete = paginatedBills.filter(b => selectedBillIds.has(b.id));
    if (billsToDelete.length === 0) return;

    const confirmed = window.confirm(`Permanently delete ${billsToDelete.length} bill(s)? This cannot be undone.`);
    if (!confirmed) return;

    setOperationInProgress({ type: 'bulk-delete', status: 'processing' });
    let successCount = 0;
    let failCount = 0;

    try {
      for (const bill of billsToDelete) {
        try {
          const canProceed = await rateLimiter.canPerformOperation(
            'DELETE',
            bill.id,
            userEmail,
            Date.now(),
            bill,
            null
          );

          if (!canProceed.allowed) {
            failCount++;
            continue;
          }

          // Delete file from Cloud Storage if present
          if (bill.fileUrl) {
            try {
              await deleteObject(ref(storage, bill.fileUrl));
            } catch (e) {
              logger.warn('Storage delete failed or file missing:', e);
            }
          }

          // Remove fingerprint entry for this bill
          try {
            await removeDuplicateFingerprint(db, bill);
          } catch (e) {
            logger.warn('Fingerprint cleanup failed:', e);
          }

          // Delete from Firestore
          await deleteDoc(doc(db, 'bills', bill.id));
          await rateLimiter.recordOperation('DELETE', bill.id, 'success', userEmail);
          successCount++;
        } catch (error) {
          logger.error('Error deleting bill:', bill.id, error);
          failCount++;
        }
      }

      setArchivedBills(prev => prev.filter(b => !selectedBillIds.has(b.id)));
      setBills(prev => prev.filter(b => !selectedBillIds.has(b.id)));

      setToastMessage(`✓ Deleted ${successCount} bill(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
      setShowToast(true);
      clearSelection();
    } finally {
      setOperationInProgress(null);
    }
  }, [selectedBillIds, paginatedBills, rateLimiter, userEmail, db, setBills, setToastMessage, setShowToast]);

  // Handle delete
  const handleDelete = useCallback(async (bill) => {
    // First confirmation
    if (!deleteConfirm) {
      setDeleteConfirm(bill.id);
      return;
    }

    // Second confirmation - proceed with delete
    if (!rateLimiter) {
      logger.error('Rate limiter not available');
      return;
    }

    const timestamp = Date.now();
    setOperationInProgress({ type: 'delete', billId: bill.id, status: 'checking' });

    try {
      // Layer 1-4 safety check
      const canProceed = await rateLimiter.canPerformOperation(
        'DELETE',
        bill.id,
        userEmail,
        timestamp,
        bill,
        null // after delete - bill no longer exists
      );

      if (!canProceed.allowed) {
        if (canProceed.skip) {
          setOperationInProgress(null);
          setDeleteConfirm(null);
          return;
        }

        if (canProceed.showAdminModal) {
          setAdminModalState({
            isOpen: true,
            errorDescription: canProceed.errorDescription,
            billId: bill.id
          });
        } else {
          setToastMessage(canProceed.reason);
          setShowToast(true);
        }
        setOperationInProgress(null);
        return;
      }

      setOperationInProgress({ type: 'delete', billId: bill.id, status: 'deleting' });

      // Delete file from Cloud Storage if present
      if (bill.fileUrl) {
        try {
          await deleteObject(ref(storage, bill.fileUrl));
        } catch (e) {
          // If the file doesn't exist or fails to delete, continue deleting the bill record
          logger.warn('Storage delete failed or file missing:', e);
        }
      }

      // Remove fingerprint entry for this bill
      try {
        await removeDuplicateFingerprint(db, bill);
      } catch (e) {
        logger.warn('Fingerprint cleanup failed:', e);
      }

      // Delete from Firestore
      await deleteDoc(doc(db, 'bills', bill.id));

      // Record operation
      await rateLimiter.recordOperation('DELETE', bill.id, 'success', userEmail);

      // Update local state
      setArchivedBills(prev => prev.filter(b => b.id !== bill.id));
      setBills(prev => prev.filter(b => b.id !== bill.id));

      setToastMessage(`✓ Bill permanently deleted`);
      setShowToast(true);
      setDeleteConfirm(null);
    } catch (error) {
      logger.error('Error deleting bill:', error);
      await rateLimiter.recordOperation('DELETE', bill.id, 'failed', userEmail);
      setToastMessage('Failed to delete bill. Please try again.');
      setShowToast(true);
    } finally {
      setOperationInProgress(null);
    }
  }, [rateLimiter, userEmail, db, setBills, setToastMessage, setShowToast, deleteConfirm]);

  const handleCancelDelete = () => {
    setDeleteConfirm(null);
  };

  const handleTypeToggle = (type) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTypes([]);
    setDateRange({ start: '', end: '' });
    setSortBy('archivedAt');
    setSortOrder('desc');
    setCurrentPage(1);
    try { localStorage.removeItem(ARCHIVE_FILTERS_KEY); } catch { void 0; }
  };

  const utilityTypes = useMemo(() => {
    const types = new Set(archivedBills.map(b => b.type));
    return Array.from(types).sort();
  }, [archivedBills]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${darkMode ? 'text-white' : ''}`}>
      {/* Admin Modal */}
      <AdminContactModal
        isOpen={adminModalState.isOpen}
        errorDescription={adminModalState.errorDescription}
        billId={adminModalState.billId}
        onClose={() => setAdminModalState({ isOpen: false, errorDescription: '', billId: '' })}
      />

      {/* Stats Panel */}
      <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-lg border ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-lg ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
            <AlertCircle className={`h-6 w-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Archived</p>
            <p className="text-2xl font-bold">{stats.count}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-lg ${darkMode ? 'bg-green-900' : 'bg-green-100'}`}>
            <DollarSign className={`h-6 w-6 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
          </div>
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Value</p>
            <p className="text-2xl font-bold">${stats.totalValue}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-lg ${darkMode ? 'bg-orange-900' : 'bg-orange-100'}`}>
            <Clock className={`h-6 w-6 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
          </div>
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Oldest</p>
            <p className="text-2xl font-bold">{stats.oldestDate}</p>
          </div>
        </div>
      </div>

      {/* Operation Status */}
      {operationInProgress && (
        <div className={`p-4 rounded-lg border flex items-center gap-2 ${
          darkMode 
            ? 'bg-blue-900 border-blue-700 text-blue-200' 
            : 'bg-blue-50 border-blue-200 text-blue-800'
        }`}>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">
            {operationInProgress.type === 'restore' ? 'Restoring' : 'Deleting'} bill {operationInProgress.billId}...
          </span>
        </div>
      )}

      {/* Filters */}
      <div className={`p-4 rounded-lg border space-y-4 ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        {/* Search */}
        <div className="relative">
          <Search className={`absolute left-3 top-3 h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            type="text"
            placeholder="Search by facility, address..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className={`w-full pl-10 pr-4 py-2 rounded-md border ${
              darkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        {/* Type filters */}
        {utilityTypes.length > 0 && (
          <div>
            <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Utility Type
            </p>
            <div className="flex flex-wrap gap-2">
              {utilityTypes.map(type => (
                <button
                  key={type}
                  onClick={() => handleTypeToggle(type)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedTypes.includes(type)
                      ? 'bg-blue-600 text-white'
                      : darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Date range */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              From
            </label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => {
                setDateRange(prev => ({ ...prev, start: e.target.value }));
                setCurrentPage(1);
              }}
              className={`w-full px-3 py-2 rounded-md border ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>
          <div>
            <label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              To
            </label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => {
                setDateRange(prev => ({ ...prev, end: e.target.value }));
                setCurrentPage(1);
              }}
              className={`w-full px-3 py-2 rounded-md border ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>
        </div>

        {/* Clear filters */}
        {(searchTerm || selectedTypes.length > 0 || dateRange.start || dateRange.end) && (
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedBillIds.size > 0 && (
        <div className={`p-4 rounded-lg border flex items-center justify-between ${
          darkMode 
            ? 'bg-blue-900/30 border-blue-700' 
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div className={`text-sm font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
            {selectedBillIds.size} bill{selectedBillIds.size !== 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleBulkRestore}
              disabled={operationInProgress?.type === 'bulk-restore'}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                darkMode
                  ? 'bg-green-600 hover:bg-green-700 text-white disabled:opacity-50'
                  : 'bg-green-600 hover:bg-green-700 text-white disabled:opacity-50'
              }`}
            >
              <ArchiveRestore className="mr-2 h-4 w-4" />
              Restore All
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={operationInProgress?.type === 'bulk-delete'}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                darkMode
                  ? 'bg-red-600 hover:bg-red-700 text-white disabled:opacity-50'
                  : 'bg-red-600 hover:bg-red-700 text-white disabled:opacity-50'
              }`}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete All
            </button>
            <button
              onClick={clearSelection}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                darkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Bills Table */}
      {filteredBills.length === 0 ? (
        <div className={`text-center py-12 rounded-lg border ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <ArchiveRestore className={`h-12 w-12 mx-auto mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <p className={`text-lg font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            No archived bills
          </p>
        </div>
      ) : (
        <>
          <div className={`border rounded-lg overflow-x-auto ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <table className="w-full text-sm">
              <thead>
                <tr className={darkMode ? 'bg-gray-800' : 'bg-gray-100'}>
                  <th className="px-4 py-3 text-center font-semibold w-12">
                    <input
                      type="checkbox"
                      checked={selectedBillIds.size === paginatedBills.length && paginatedBills.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded cursor-pointer"
                      title="Select all on this page"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">Facility</th>
                  <th className="px-4 py-3 text-left font-semibold cursor-pointer" onClick={() => {
                    if (sortBy === 'date') setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    else { setSortBy('date'); setSortOrder('desc'); }
                  }}>Bill Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                  <th className="px-4 py-3 text-left font-semibold">Type</th>
                  <th className="px-4 py-3 text-right font-semibold cursor-pointer" onClick={() => {
                    if (sortBy === 'amount') setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    else { setSortBy('amount'); setSortOrder('desc'); }
                  }}>Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                  <th className="px-4 py-3 text-left font-semibold">Provider</th>
                  <th className="px-4 py-3 text-left font-semibold cursor-pointer" onClick={() => {
                    if (sortBy === 'archivedAt') setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    else { setSortBy('archivedAt'); setSortOrder('desc'); }
                  }}>Archived {sortBy === 'archivedAt' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                  <th className="px-4 py-3 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBills.map((bill) => (
                  <tr
                    key={bill.id}
                    onClick={() => bill.fileUrl && onSecureView?.(bill.fileUrl)}
                    className={`border-t ${selectedBillIds.has(bill.id) ? (darkMode ? 'bg-blue-900/30' : 'bg-blue-50') : ''} ${
                      darkMode
                        ? 'border-gray-700 hover:bg-gray-750'
                        : 'border-gray-200 hover:bg-gray-50'
                    } ${bill.fileUrl && onSecureView ? 'cursor-pointer' : ''}`}
                  >
                    <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedBillIds.has(bill.id)}
                        onChange={() => toggleBillSelection(bill.id)}
                        className="rounded cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">{bill.site_name}</td>
                    <td className="px-4 py-3">{new Date(bill.date).toLocaleDateString()}</td>
                    <td className="px-4 py-3">{bill.type}</td>
                    <td className="px-4 py-3 text-right font-semibold">${parseFloat(bill.amount).toFixed(2)}</td>
                    <td className="px-4 py-3">{bill.provider}</td>
                    <td className="px-4 py-3 text-sm">{(bill.archivedAt?.toDate ? bill.archivedAt.toDate() : new Date(bill.archivedAt)).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-center" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => handleRestoreSingle(bill)}
                          disabled={operationInProgress?.billId === bill.id || deleteConfirm === bill.id || selectedBillIds.size > 0}
                          className="p-1 hover:bg-green-100 dark:hover:bg-green-900 rounded transition-colors disabled:opacity-50"
                          title="Restore bill"
                        >
                          <ArchiveRestore className="h-4 w-4 text-green-600 dark:text-green-400" />
                        </button>
                        <button
                          onClick={() => handleDelete(bill)}
                          disabled={operationInProgress?.billId === bill.id || selectedBillIds.size > 0}
                          className="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded transition-colors disabled:opacity-50"
                          title="Delete bill"
                        >
                          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                        </button>
                      </div>
                      {deleteConfirm === bill.id && (
                        <div className={`absolute mt-2 p-3 rounded-lg border z-10 ${
                          darkMode
                            ? 'bg-gray-800 border-gray-700'
                            : 'bg-white border-gray-200 shadow-lg'
                        }`}>
                          <p className="text-xs font-semibold mb-2">Permanently delete?</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleDelete(bill)}
                              className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded"
                            >
                              Delete
                            </button>
                            <button
                              onClick={handleCancelDelete}
                              className={`px-2 py-1 text-xs rounded ${
                                darkMode
                                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                                  : 'bg-gray-200 hover:bg-gray-300'
                              }`}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className={`flex items-center justify-between p-4 rounded-lg border ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center gap-4">
              <label className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Per page:
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className={`ml-2 px-3 py-1 rounded-md border ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300'
                  }`}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </label>
              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredBills.length)} of {filteredBills.length}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`p-2 rounded transition-colors disabled:opacity-50 ${
                  darkMode
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-200'
                }`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`p-2 rounded transition-colors disabled:opacity-50 ${
                  darkMode
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-200'
                }`}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ArchiveViewer;
